/**
 * 공급자 중립 관측성 파이프라인.
 *
 * logger는 이벤트만 발행하고, 앱 부팅 시 middleware와 sink를 조합한다. Sentry,
 * Datadog, OTLP, DB EventLog 같은 실제 전달처는 이 모듈에 의존하지 않는다.
 */

import { prerenderSafeNowIso, prerenderSafeNowMs } from "./time";

export type ObservabilityLevel = "debug" | "info" | "warn" | "error";

export type ObservabilityTagValue = string | number | boolean;

export type ObservabilityRequest = {
  id?: string;
  method?: string;
  path?: string;
  routePath?: string;
  routeType?: string;
};

export type ObservabilityActor = {
  id?: string;
  type?: string;
};

export type ObservabilityEvent = {
  level: ObservabilityLevel;
  message: string;
  timestamp: string;
  /** 이벤트가 발생한 경로. 예: logger, server-action, hono, next.request */
  source: string;
  /** 애플리케이션이 오류를 잡아 응답으로 변환했는지 여부 */
  handled: boolean;
  context?: string;
  error?: unknown;
  tags?: Record<string, ObservabilityTagValue>;
  attributes?: Record<string, unknown>;
  actor?: ObservabilityActor;
  request?: ObservabilityRequest;
  fingerprint?: readonly string[];
};

export type ObservabilityEventInput = Omit<ObservabilityEvent, "timestamp"> & {
  timestamp?: string;
};

export type ObservabilityNext = (event: ObservabilityEvent) => Promise<void>;

export type ObservabilityMiddleware = {
  name: string;
  handle: (
    event: ObservabilityEvent,
    next: ObservabilityNext,
  ) => void | Promise<void>;
};

export type ObservabilitySink = {
  name: string;
  levels?: readonly ObservabilityLevel[];
  /** source나 태그를 기준으로 공급자별 라우팅이 필요할 때 사용한다. */
  accepts?: (event: ObservabilityEvent) => boolean;
  /** background sink는 응답을 막지 않고 scheduler로 수명만 연장한다. */
  delivery?: "blocking" | "background";
  /** blocking sink의 최대 대기 시간. 실제 외부 작업을 취소하지는 않는다. */
  timeoutMs?: number;
  capture: (event: ObservabilityEvent) => void | Promise<void>;
  /**
   * 현재 스코프(서버는 요청, 브라우저는 세션)의 주체를 sink에 알린다.
   * `capture`와 달리 이벤트를 발행하지 않고, 이후 같은 스코프에서 수집되는
   * 이벤트에 주체 정보가 따라붙게 만드는 것이 목적이다. 주체를 지우려면 null.
   * 동기 계약 — 요청 경로에서 호출되므로 I/O를 해서는 안 된다.
   */
  identify?: (actor: ObservabilityActor | null) => void;
};

export type ObservabilityFailure = {
  stage: "middleware" | "sink" | "scheduler" | "identify";
  plugin: string;
  error: unknown;
  /** identify 단계 실패에는 원인 이벤트가 없다. */
  event?: ObservabilityEvent;
};

export type ObservabilityPipelineOptions = {
  middleware?: readonly ObservabilityMiddleware[];
  sinks?: readonly ObservabilitySink[];
  /** Vercel waitUntil 같은 런타임별 background task 수명 연장 훅. */
  schedule?: (task: Promise<void>) => void;
  onFailure?: (failure: ObservabilityFailure) => void;
};

export type ReportErrorOptions = {
  message?: string;
  source?: string;
  handled?: boolean;
  context?: string;
  tags?: Record<string, ObservabilityTagValue>;
  attributes?: Record<string, unknown>;
  actor?: ObservabilityActor;
  request?: ObservabilityRequest;
  fingerprint?: readonly string[];
};

type ActivePipeline = Required<
  Pick<ObservabilityPipelineOptions, "middleware" | "sinks">
> &
  Pick<ObservabilityPipelineOptions, "schedule" | "onFailure">;

const EMPTY_PIPELINE: ActivePipeline = {
  middleware: [],
  sinks: [],
};

/**
 * 파이프라인 상태는 모듈 지역 변수가 아니라 프로세스 전역에 둔다.
 *
 * Next는 `instrumentation.ts`와 앱/RSC 코드를 별개의 모듈 그래프로 번들해서,
 * 같은 `@reopt/logger/observability`라도 **서로 다른 모듈 인스턴스**가 된다
 * (dev·production 빌드 모두 실측 확인). 모듈 지역 변수에 담으면
 * instrumentation이 구성한 sink가 앱 코드에는 영원히 빈 파이프라인으로 보여,
 * 앱·feature 패키지에서 발행한 이벤트가 조용히 유실된다.
 *
 * `Symbol.for`는 프로세스 단위 전역 레지스트리라 인스턴스가 몇 개로 쪼개지든
 * 같은 홀더를 가리킨다. `packages/db`가 Database 인스턴스 싱글턴에 쓰는 것과 같은
 * 패턴이다.
 */
const PIPELINE_HOLDER_KEY = Symbol.for("reopt.observability.pipeline");

type PipelineHolder = { current: ActivePipeline };

const globalForObservability = globalThis as typeof globalThis & {
  [PIPELINE_HOLDER_KEY]?: PipelineHolder;
};

const pipelineHolder: PipelineHolder = (globalForObservability[
  PIPELINE_HOLDER_KEY
] ??= { current: EMPTY_PIPELINE });

const REDACTED = "[REDACTED]";
const CIRCULAR = "[Circular]";
const TRUNCATED = "[Truncated]";
const UNSERIALIZABLE = "[Unserializable]";

const SENSITIVE_KEYS = new Set([
  "authorization",
  "cookie",
  "cookies",
  "setcookie",
  "password",
  "passwd",
  "secret",
  "clientsecret",
  "apikey",
  "accesstoken",
  "refreshtoken",
  "idtoken",
  "sessiontoken",
  "token",
  "privatekey",
  "credential",
  "credentials",
  "email",
  "phone",
  "phonenumber",
]);

const SENSITIVE_QUERY_KEYS =
  "authorization|password|passwd|secret|client[_-]?secret|api[_-]?key|token|auth[_-]?token|access[_-]?token|refresh[_-]?token|id[_-]?token|session[_-]?token";

const SENSITIVE_ASSIGNMENT_PATTERN = new RegExp(
  `\\b(${SENSITIVE_QUERY_KEYS})\\b(\\s*[:=]\\s*)([^\\s,;&]+)`,
  "gi",
);
const SENSITIVE_QUERY_PATTERN = new RegExp(
  `([?&](?:${SENSITIVE_QUERY_KEYS})=)[^&#\\s]*`,
  "gi",
);
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+\/-]+=*/gi;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const URL_CREDENTIAL_PATTERN = /([a-z][a-z0-9+.-]*:\/\/[^:\s/]+:)[^@\s/]+@/gi;

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[-_.]/g, "");
}

function isSensitiveKey(key: string): boolean {
  const normalized = normalizeKey(key);
  return (
    SENSITIVE_KEYS.has(normalized) ||
    normalized.endsWith("token") ||
    normalized.endsWith("secret") ||
    normalized.endsWith("password") ||
    normalized.endsWith("credential") ||
    normalized.endsWith("apikey") ||
    normalized.endsWith("email") ||
    normalized.endsWith("phone") ||
    normalized.endsWith("phonenumber")
  );
}

function redactText(value: string, maxLength: number): string {
  const redacted = value
    .replace(BEARER_PATTERN, `Bearer ${REDACTED}`)
    .replace(JWT_PATTERN, REDACTED)
    .replace(URL_CREDENTIAL_PATTERN, `$1${REDACTED}@`)
    .replace(SENSITIVE_QUERY_PATTERN, `$1${REDACTED}`)
    .replace(SENSITIVE_ASSIGNMENT_PATTERN, `$1$2${REDACTED}`);

  if (redacted.length <= maxLength) return redacted;
  return `${redacted.slice(0, maxLength)}${TRUNCATED}`;
}

type RedactionState = {
  depth: number;
  maxDepth: number;
  maxArrayLength: number;
  maxStringLength: number;
  seen: WeakSet<object>;
};

function redactValue(
  value: unknown,
  key: string | undefined,
  state: RedactionState,
): unknown {
  if (key && isSensitiveKey(key)) return REDACTED;

  if (typeof value === "string") {
    return redactText(value, state.maxStringLength);
  }
  if (
    value === null ||
    value === undefined ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "symbol" || typeof value === "function") {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();

  if (state.depth >= state.maxDepth) return TRUNCATED;
  if (state.seen.has(value)) return CIRCULAR;
  state.seen.add(value);

  const nextState = { ...state, depth: state.depth + 1 };

  if (value instanceof Error) {
    const errorRecord = value as Error & {
      code?: unknown;
      digest?: unknown;
      meta?: unknown;
      cause?: unknown;
      status?: unknown;
      statusCode?: unknown;
      body?: unknown;
    };
    // APIError can have an empty message and carry its reason only in body.
    // Copy diagnostic scalars only; never collect the entire response body.
    const body = errorRecord.body;
    const apiDiagnostics: Record<string, unknown> = {};
    if (body && typeof body === "object") {
      for (const [field, label] of [
        ["error", "apiError"],
        ["error_description", "apiErrorDescription"],
        ["code", "apiCode"],
        ["message", "apiMessage"],
      ] as const) {
        const value = (body as Record<string, unknown>)[field];
        if (typeof value === "string") {
          apiDiagnostics[label] = redactValue(value, label, nextState);
        }
      }
    }
    return {
      name: redactValue(errorRecord.name, "name", nextState),
      message: redactValue(errorRecord.message, "message", nextState),
      stack: redactValue(errorRecord.stack, "stack", nextState),
      ...apiDiagnostics,
      ...(typeof errorRecord.status === "string"
        ? { status: redactValue(errorRecord.status, "status", nextState) }
        : {}),
      ...(typeof errorRecord.statusCode === "number"
        ? { statusCode: errorRecord.statusCode }
        : {}),
      ...(errorRecord.code !== undefined
        ? { code: redactValue(errorRecord.code, "code", nextState) }
        : {}),
      ...(errorRecord.digest !== undefined
        ? { digest: redactValue(errorRecord.digest, "digest", nextState) }
        : {}),
      ...(errorRecord.meta !== undefined
        ? { meta: redactValue(errorRecord.meta, "meta", nextState) }
        : {}),
      ...(errorRecord.cause !== undefined
        ? { cause: redactValue(errorRecord.cause, "cause", nextState) }
        : {}),
    };
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, state.maxArrayLength)
      .map((item) => redactValue(item, undefined, nextState));
  }

  const result: Record<string, unknown> = {};
  for (const [entryKey, entryValue] of Object.entries(value)) {
    result[entryKey] = redactValue(entryValue, entryKey, nextState);
  }
  return result;
}

/** 순환 참조와 비-JSON 값을 정규화하면서 보안 관련 필드를 제거한다. */
export function redactSensitiveData<T>(
  value: T,
  options: {
    maxDepth?: number;
    maxArrayLength?: number;
    maxStringLength?: number;
  } = {},
): T {
  try {
    return redactValue(value, undefined, {
      depth: 0,
      maxDepth: options.maxDepth ?? 8,
      maxArrayLength: options.maxArrayLength ?? 50,
      maxStringLength: options.maxStringLength ?? 8_000,
      seen: new WeakSet(),
    }) as T;
  } catch {
    return UNSERIALIZABLE as T;
  }
}

/** DB/로그 sink에서 원본 Error 대신 쓸 안전한 구조화 표현. */
export function describeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    const described = redactSensitiveData(error) as unknown;
    return described &&
      typeof described === "object" &&
      !Array.isArray(described)
      ? (described as Record<string, unknown>)
      : { value: described };
  }
  return { value: redactSensitiveData(error) };
}

function notifyFailure(
  pipeline: ActivePipeline,
  failure: ObservabilityFailure,
): void {
  try {
    pipeline.onFailure?.(failure);
  } catch {
    // 관측성 실패 콜백도 본 애플리케이션 흐름을 깨면 안 된다.
  }
}

async function withTimeout(
  task: Promise<void>,
  timeoutMs: number | undefined,
): Promise<void> {
  if (!timeoutMs || timeoutMs <= 0) {
    await task;
    return;
  }

  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      task,
      new Promise<never>((_, reject) => {
        timeout = setTimeout(
          () =>
            reject(
              new Error(`Observability sink timed out after ${timeoutMs}ms`),
            ),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

async function deliverToSink(
  pipeline: ActivePipeline,
  sink: ObservabilitySink,
  event: ObservabilityEvent,
): Promise<void> {
  if (sink.levels && !sink.levels.includes(event.level)) return;

  try {
    if (sink.accepts && !sink.accepts(event)) return;
  } catch (error) {
    notifyFailure(pipeline, { stage: "sink", plugin: sink.name, error, event });
    return;
  }

  let task: Promise<void>;
  try {
    task = Promise.resolve(sink.capture(event));
  } catch (error) {
    notifyFailure(pipeline, { stage: "sink", plugin: sink.name, error, event });
    return;
  }

  const guardedTask = withTimeout(task, sink.timeoutMs).catch((error) => {
    notifyFailure(pipeline, { stage: "sink", plugin: sink.name, error, event });
  });

  if (sink.delivery === "background") {
    if (pipeline.schedule) {
      try {
        pipeline.schedule(guardedTask);
      } catch (error) {
        notifyFailure(pipeline, {
          stage: "scheduler",
          plugin: sink.name,
          error,
          event,
        });
      }
    }
    // schedule이 없거나 실패해도 이미 시작된 Promise의 rejection은 guardedTask가 흡수한다.
    void guardedTask;
    return;
  }

  await guardedTask;
}

async function deliver(
  pipeline: ActivePipeline,
  event: ObservabilityEvent,
): Promise<void> {
  await Promise.all(
    pipeline.sinks.map((sink) => deliverToSink(pipeline, sink, event)),
  );
}

/**
 * 파이프라인을 원자적으로 교체한다. Next.js register 재실행이나 테스트 격리 시에도
 * sink가 중복 등록되지 않는다. 반환 함수는 현재 설정이 그대로일 때만 복원한다.
 */
export function configureObservability(
  options: ObservabilityPipelineOptions,
): () => void {
  const previous = pipelineHolder.current;
  const configured: ActivePipeline = {
    middleware: [...(options.middleware ?? [])],
    sinks: [...(options.sinks ?? [])],
    schedule: options.schedule,
    onFailure: options.onFailure,
  };
  pipelineHolder.current = configured;

  return () => {
    if (pipelineHolder.current === configured)
      pipelineHolder.current = previous;
  };
}

/** 테스트와 런타임 teardown용. */
export function resetObservability(): void {
  pipelineHolder.current = EMPTY_PIPELINE;
}

/**
 * 현재 스코프의 주체를 sink들에 전파한다.
 *
 * 서버에서는 요청 단위 스코프(세션 조회 chokepoint), 브라우저에서는 세션이
 * 확정되는 시점에 한 번 호출하는 것을 전제로 한다. 이후 같은 스코프에서
 * 수집되는 오류에 주체가 따라붙어 "누구에게 터졌는가"를 식별할 수 있다.
 *
 * 이벤트 발행이 아니므로 middleware(리댁션·dedupe)를 타지 않는다 — 호출자가
 * 식별자 외의 정보를 넘기지 않을 책임을 진다. 관측성 실패는 호출자에게
 * 전파하지 않는다.
 */
export function identifyActor(actor: ObservabilityActor | null): void {
  const pipeline = pipelineHolder.current;

  for (const sink of pipeline.sinks) {
    if (!sink.identify) continue;
    try {
      sink.identify(actor);
    } catch (error) {
      notifyFailure(pipeline, {
        stage: "identify",
        plugin: sink.name,
        error,
      });
    }
  }
}

/** 모든 이벤트 발행의 단일 진입점. 관측성 실패는 호출자에게 전파하지 않는다. */
export async function publishObservabilityEvent(
  input: ObservabilityEventInput,
): Promise<void> {
  const pipeline = pipelineHolder.current;
  if (pipeline.sinks.length === 0) return;

  const event: ObservabilityEvent = {
    ...input,
    timestamp: input.timestamp ?? prerenderSafeNowIso(),
  };

  const terminal: ObservabilityNext = (nextEvent) =>
    deliver(pipeline, nextEvent);
  const composed = [...pipeline.middleware].reverse().reduce<ObservabilityNext>(
    (next, middleware) => async (nextEvent) => {
      let forwarded = false;
      const guardedNext: ObservabilityNext = async (forwardedEvent) => {
        forwarded = true;
        await next(forwardedEvent);
      };
      try {
        await middleware.handle(nextEvent, guardedNext);
      } catch (error) {
        notifyFailure(pipeline, {
          stage: "middleware",
          plugin: middleware.name,
          error,
          event: nextEvent,
        });
        // middleware가 전달 전에 실패하면 원본 이벤트로 fail-open한다. 이미 next를
        // 호출한 뒤 실패한 경우에는 중복 전달을 만들지 않는다.
        if (!forwarded) await next(nextEvent);
      }
    },
    terminal,
  );

  await composed(event);
}

export function reportError(
  error: unknown,
  options: ReportErrorOptions = {},
): Promise<void> {
  const details = describeError(error);
  const fallbackMessage =
    typeof details.message === "string" ? details.message : String(error);

  return publishObservabilityEvent({
    level: "error",
    message: options.message ?? fallbackMessage,
    source: options.source ?? "manual",
    handled: options.handled ?? true,
    context: options.context,
    error,
    tags: options.tags,
    attributes: options.attributes,
    actor: options.actor,
    request: options.request,
    fingerprint: options.fingerprint,
  });
}

/** 공통 보안 필터. 원본 Error는 provider SDK가 처리하도록 유지한다. */
export function createRedactionMiddleware(): ObservabilityMiddleware {
  return {
    name: "redaction",
    async handle(event, next) {
      const sanitized = redactSensitiveData({
        ...event,
        error: undefined,
      });
      if (
        !sanitized ||
        typeof sanitized !== "object" ||
        Array.isArray(sanitized)
      ) {
        await next({
          level: event.level,
          message: redactText(event.message, 8_000),
          timestamp: event.timestamp,
          source: event.source,
          handled: event.handled,
          context: event.context,
          error: event.error,
          attributes: { redactionError: UNSERIALIZABLE },
        });
        return;
      }
      await next({ ...sanitized, error: event.error });
    },
  };
}

/** 동일 Error 인스턴스가 logger 후 rethrow 되는 경우의 단기 중복 보고를 막는다. */
export function createErrorDedupeMiddleware(
  windowMs = 5_000,
): ObservabilityMiddleware {
  const seen = new WeakMap<object, number>();

  return {
    name: "error-dedupe",
    async handle(event, next) {
      if (
        event.error === null ||
        (typeof event.error !== "object" && typeof event.error !== "function")
      ) {
        await next(event);
        return;
      }

      // Date.now()는 프리렌더 스코프에서 unstable value로 차단된다 (./time 참조)
      const now = prerenderSafeNowMs();
      const previous = seen.get(event.error);
      if (previous !== undefined && now - previous < windowMs) return;

      seen.set(event.error, now);
      await next(event);
    },
  };
}

/** 앱명·런타임·배포 정보 같은 공통 필드를 이벤트별 값보다 낮은 우선순위로 합친다. */
export function createEnrichmentMiddleware(
  name: string,
  enrichment:
    | Partial<ObservabilityEvent>
    | ((event: ObservabilityEvent) => Partial<ObservabilityEvent>),
): ObservabilityMiddleware {
  return {
    name,
    async handle(event, next) {
      const patch =
        typeof enrichment === "function" ? enrichment(event) : enrichment;
      await next({
        ...patch,
        ...event,
        tags: { ...patch.tags, ...event.tags },
        attributes: { ...patch.attributes, ...event.attributes },
        actor:
          patch.actor || event.actor
            ? { ...patch.actor, ...event.actor }
            : undefined,
        request:
          patch.request || event.request
            ? { ...patch.request, ...event.request }
            : undefined,
      });
    },
  };
}
